import { useState, useEffect, useMemo, useRef } from "react";
import { ImageOff, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { thumbUrl } from "@/lib/img-thumb";

/**
 * Resilient <img> for static-ad tiles and viewers.
 *
 * Ad grids historically loaded many full-size tiles directly from the
 * rate-limited Cloudflare `pub-…r2.dev` endpoint, so a burst of simultaneous
 * requests got some tiles throttled; a single immediate retry also landed
 * inside the same burst, and the component then stuck as a permanent blank
 * placeholder until a full page refresh (diagnosed in task #2351). This
 * component now mitigates the delivery symptom in three ways:
 *
 *   - `thumbWidth` (grid tiles): loads a server-resized webp through
 *     `/api/img-proxy` instead of the raw R2 URL, shrinking a ~30-tile burst
 *     from ~80 MB of PNGs to a few MB and taking the browser off the
 *     rate-limited public endpoint entirely (the proxy caches thumbnails in
 *     R2). If the proxied thumbnail fails, it falls back to the original URL.
 *   - Retries happen on a backoff schedule instead of immediately, so they
 *     land AFTER the burst has passed. Cache-bust tokens are captured once
 *     per attempt and stored in state, so URLs stay stable across parent
 *     re-renders — polling never triggers extra refetches.
 *   - If every attempt fails it renders a VISIBLE broken state with a
 *     "Tap to reload" affordance that restarts the attempt cycle — never a
 *     silent near-white card that only a page refresh can fix.
 *
 * It is a near drop-in for a raw <img>: `className`, `style`, `loading`,
 * `data-testid`, `onLoad`, `onError`, etc. all pass through. `src` accepts
 * null/undefined and renders the (non-interactive) placeholder in that case.
 */
function appendCacheBust(url: string, token: number): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_r=${token}`;
}

type Attempt = {
  /** Which base URL this attempt loads. */
  kind: "thumb" | "full";
  /** Append a cache-bust token so the browser re-requests the URL. */
  bust: boolean;
  /** Wait this long after the previous failure before trying (ms). */
  delayMs: number;
};

/**
 * Attempt schedule. With a thumbnail width the proxied thumb is tried first
 * (retry once on a short backoff), then the original URL (also with one
 * backoff retry). Without a thumbnail, the original gets two backoff retries.
 * Delays are deliberately >1s so retries land after a completion burst.
 */
function buildAttempts(hasThumb: boolean): Attempt[] {
  if (hasThumb) {
    return [
      { kind: "thumb", bust: false, delayMs: 0 },
      { kind: "thumb", bust: true, delayMs: 1500 },
      { kind: "full", bust: false, delayMs: 1500 },
      { kind: "full", bust: true, delayMs: 5000 },
    ];
  }
  return [
    { kind: "full", bust: false, delayMs: 0 },
    { kind: "full", bust: true, delayMs: 1500 },
    { kind: "full", bust: true, delayMs: 5000 },
  ];
}

type AdImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string | null | undefined;
  /**
   * When set, grid-tile mode: load a `/api/img-proxy` resized webp of `src`
   * at this width first, falling back to the original URL. Use for many-tile
   * grids; leave unset for full-size viewers (lightboxes, edit modals).
   */
  thumbWidth?: number;
  /** Extra classes applied only to the fallback placeholder box. */
  placeholderClassName?: string;
};

export function AdImage({
  src: rawSrc,
  alt = "",
  className,
  style,
  loading = "lazy",
  onError,
  thumbWidth,
  placeholderClassName,
  ...rest
}: AdImageProps) {
  // Catalog JSON is not guaranteed to satisfy the compile-time URL type.
  const src = typeof rawSrc === "string" && rawSrc.trim() ? rawSrc : null;
  // Proxied thumbnail URL, or null when it wouldn't differ from `src`
  // (non-https values pass through thumbUrl unchanged — no point retrying
  // the identical URL twice).
  const thumb = useMemo(() => {
    if (!src || !thumbWidth) return null;
    const t = thumbUrl(src, thumbWidth);
    return t && t !== src ? t : null;
  }, [src, thumbWidth]);

  const attempts = useMemo(() => buildAttempts(!!thumb), [thumb]);

  // Index into `attempts`; `attempts.length` = exhausted → broken state.
  const [attemptIdx, setAttemptIdx] = useState(0);
  // Cache-bust token captured once when advancing into a bust attempt so the
  // URL is stable across re-renders (recomputing per render would refetch).
  const [bustToken, setBustToken] = useState<number | null>(null);
  // Bumped by "Tap to reload" so the whole cycle restarts with fresh tokens.
  const [reloadNonce, setReloadNonce] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the retry cycle whenever the source changes (e.g. regenerate/edit).
  useEffect(() => {
    setAttemptIdx(0);
    setBustToken(null);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [src, thumb, reloadNonce]);

  const testId = (rest as Record<string, unknown>)["data-testid"] as
    | string
    | undefined;

  const exhausted = attemptIdx >= attempts.length;

  if (!src || exhausted) {
    const placeholder = (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 bg-muted/40 text-muted-foreground/60 min-h-[80px] min-w-[80px]",
          className,
          placeholderClassName,
        )}
        style={style}
        role="img"
        aria-label={typeof alt === "string" && alt ? alt : "Image unavailable"}
        data-testid={testId}
      >
        <ImageOff className="h-8 w-8" />
        {src ? (
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <RotateCcw className="h-3 w-3" />
            Tap to reload
          </span>
        ) : null}
      </div>
    );
    if (!src) return placeholder;
    // Broken-after-retries: visible + recoverable in place.
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label="Reload image"
        className="block w-full h-full cursor-pointer"
        onClick={(e) => {
          // Don't trigger the tile's own open/preview handler underneath.
          e.stopPropagation();
          e.preventDefault();
          setReloadNonce((n) => n + 1);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            e.preventDefault();
            setReloadNonce((n) => n + 1);
          }
        }}
        data-testid={testId ? `${testId}-reload` : undefined}
      >
        {placeholder}
      </span>
    );
  }

  const attempt = attempts[attemptIdx];
  const baseUrl = attempt.kind === "thumb" && thumb ? thumb : src;
  const resolvedSrc =
    attempt.bust && bustToken !== null
      ? appendCacheBust(baseUrl, bustToken)
      : baseUrl;

  return (
    <img
      {...rest}
      // Force a fresh element per attempt so the browser re-requests the URL.
      key={`${reloadNonce}:${attemptIdx}`}
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      onError={(e) => {
        onError?.(e);
        // Each attempt remounts the <img> (via key), so this handler's
        // closure sees the attempt that actually errored.
        const next = attemptIdx + 1;
        if (next >= attempts.length) {
          setAttemptIdx(next); // exhausted → broken state
          return;
        }
        const delay = attempts[next].delayMs;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setBustToken(Date.now());
          setAttemptIdx(next);
        }, delay);
      }}
    />
  );
}
